import type { Furniture } from "../furniture-list";
import type {
  DynamicFurnitureShape,
  DynamicTableLayout,
  Shape
} from "../types";

export const DEFAULT_TABLE_LAYOUT: DynamicTableLayout = {
  version: 1,
  shape: "round",
  tableWidth: 160,
  tableHeight: 160,
  chairs: 6,
  top: 3,
  bottom: 3,
  left: 1,
  right: 1,
  chairStyle: "standard",
  chairScale: 1,
  gap: 4,
  cornerRadius: 8,
  startAngle: -90
};
const bounded = (n: number, fallback: number, min: number, max: number) =>
  Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : fallback;
export function normalizeTableLayout(
  input: DynamicTableLayout
): DynamicTableLayout {
  const count = (n: number) => Math.round(bounded(n, 0, 0, 24));
  const width = bounded(input.tableWidth, 160, 4, 4000);
  return {
    ...DEFAULT_TABLE_LAYOUT,
    ...input,
    version: 1,
    shape: input.shape === "rectangle" ? "rectangle" : "round",
    tableWidth: width,
    tableHeight:
      input.shape === "rectangle"
        ? bounded(input.tableHeight, 100, 4, 4000)
        : width,
    chairs: count(input.chairs),
    top: count(input.top),
    bottom: count(input.bottom),
    left: count(input.left),
    right: count(input.right),
    chairStyle: input.chairStyle === "large" ? "large" : "standard",
    chairScale: bounded(input.chairScale, 1, 0.05, 10),
    gap: bounded(input.gap, 4, 0, 100),
    cornerRadius: bounded(input.cornerRadius, 8, 0, 200),
    startAngle: bounded(input.startAngle, -90, -360, 360)
  };
}
export interface TablePart {
  key: string;
  kind: "table" | "chair" | "chair-back" | "chair-arm";
  ellipse?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  radius: number;
}
/** Single source of geometry for Konva, SVG, previews and detaching. */
export function layoutDynamicTable(input: DynamicTableLayout) {
  const p = normalizeTableLayout(input);
  const chairWidth = (p.chairStyle === "large" ? 42 : 32) * p.chairScale;
  const chairDepth = (p.chairStyle === "large" ? 38 : 28) * p.chairScale;
  const total =
    p.shape === "round" ? p.chairs : p.top + p.bottom + p.left + p.right;
  const margin = total ? chairDepth + p.gap + chairWidth / 2 : 0;
  const width = p.tableWidth + 2 * margin,
    height = p.tableHeight + 2 * margin;
  const cx = width / 2,
    cy = height / 2;
  const parts: TablePart[] = [];
  let seatIndex = 0;
  const seat = (x: number, y: number, rotation: number) => {
    const index = seatIndex++;
    const a = (rotation * Math.PI) / 180;
    const add = (
      kind: TablePart["kind"],
      px: number,
      py: number,
      w: number,
      h: number,
      r: number
    ) =>
      parts.push({
        key: `${index}-${kind}-${px}`,
        kind,
        x: x + px * Math.cos(a) - py * Math.sin(a),
        y: y + px * Math.sin(a) + py * Math.cos(a),
        width: w,
        height: h,
        rotation,
        radius: r
      });
    // Local chair faces down; its back is the outward edge, towards local -y.
    add(
      "chair",
      -chairWidth / 2,
      -chairDepth / 2,
      chairWidth,
      chairDepth,
      4 * p.chairScale
    );
    add(
      "chair-back",
      -chairWidth / 2,
      -chairDepth / 2,
      chairWidth,
      5 * p.chairScale,
      2 * p.chairScale
    );
    if (p.chairStyle === "large") {
      add(
        "chair-arm",
        -chairWidth / 2,
        -chairDepth / 2 + 4 * p.chairScale,
        4 * p.chairScale,
        chairDepth - 8 * p.chairScale,
        2 * p.chairScale
      );
      add(
        "chair-arm",
        chairWidth / 2 - 4 * p.chairScale,
        -chairDepth / 2 + 4 * p.chairScale,
        4 * p.chairScale,
        chairDepth - 8 * p.chairScale,
        2 * p.chairScale
      );
    }
  };
  if (p.shape === "round") {
    const r = p.tableWidth / 2 + p.gap + chairDepth / 2;
    for (let i = 0; i < p.chairs; i++) {
      const degrees = p.startAngle + (i * 360) / p.chairs;
      const a = (degrees * Math.PI) / 180;
      seat(cx + r * Math.cos(a), cy + r * Math.sin(a), degrees + 90);
    }
  } else {
    const row = (
      count: number,
      length: number,
      callback: (offset: number) => void
    ) => {
      for (let i = 0; i < count; i++)
        callback(((i + 0.5) * length) / count - length / 2);
    };
    row(p.top, p.tableWidth, (x) =>
      seat(cx + x, margin - p.gap - chairDepth / 2, 0)
    );
    row(p.bottom, p.tableWidth, (x) =>
      seat(cx + x, margin + p.tableHeight + p.gap + chairDepth / 2, 180)
    );
    row(p.left, p.tableHeight, (y) =>
      seat(margin - p.gap - chairDepth / 2, cy + y, -90)
    );
    row(p.right, p.tableHeight, (y) =>
      seat(margin + p.tableWidth + p.gap + chairDepth / 2, cy + y, 90)
    );
  }
  parts.push({
    key: "table",
    kind: "table",
    ellipse: p.shape === "round",
    x: margin,
    y: margin,
    width: p.tableWidth,
    height: p.tableHeight,
    rotation: 0,
    radius: Math.min(p.cornerRadius, p.tableWidth / 2, p.tableHeight / 2)
  });
  const crowded =
    p.shape === "round"
      ? p.chairs > 1 &&
        2 *
          (p.tableWidth / 2 + p.gap + chairDepth / 2) *
          Math.sin(Math.PI / p.chairs) <
          chairWidth + 4
      : Math.max(p.top, p.bottom) * (chairWidth + 4) > p.tableWidth ||
        Math.max(p.left, p.right) * (chairWidth + 4) > p.tableHeight;
  return { width, height, parts, layout: p, crowded };
}
export function createDynamicFurniture(
  layout: DynamicTableLayout = DEFAULT_TABLE_LAYOUT,
  position = { x: 0, y: 0 }
): DynamicFurnitureShape {
  const geometry = layoutDynamicTable(layout);
  return {
    id: crypto.randomUUID(),
    type: "dynamic-furniture",
    ...position,
    width: geometry.width,
    height: geometry.height,
    layout: geometry.layout,
    layerType: "shapes",
    layerLabel: "Table set",
    color: "#E8ECEF"
  };
}
export function editDynamicFurniture(
  shape: DynamicFurnitureShape,
  patch: Partial<DynamicTableLayout>
): DynamicFurnitureShape {
  const geometry = layoutDynamicTable({ ...shape.layout, ...patch });
  const a = ((shape.rotation || 0) * Math.PI) / 180;
  const dx = ((shape.width - geometry.width) / 2) * (shape.scaleX ?? 1),
    dy = ((shape.height - geometry.height) / 2) * (shape.scaleY ?? 1);
  return {
    ...shape,
    layout: geometry.layout,
    width: geometry.width,
    height: geometry.height,
    x: shape.x + dx * Math.cos(a) - dy * Math.sin(a),
    y: shape.y + dx * Math.sin(a) + dy * Math.cos(a)
  };
}
export function detachDynamicFurniture(shape: DynamicFurnitureShape): Shape {
  const g = layoutDynamicTable(shape.layout);
  return {
    id: shape.id,
    type: "group",
    x: shape.x,
    y: shape.y,
    width: g.width,
    height: g.height,
    rotation: shape.rotation,
    scaleX: ((shape.scaleX ?? 1) * shape.width) / g.width,
    scaleY: ((shape.scaleY ?? 1) * shape.height) / g.height,
    layerType: "shapes",
    layerLabel: "Detached table set",
    children: g.parts.map((p) => ({
      id: crypto.randomUUID(),
      type: p.ellipse ? "circle" : "rectangle",
      x: p.x + (p.ellipse ? p.width / 2 : 0),
      y: p.y + (p.ellipse ? p.height / 2 : 0),
      width: p.width,
      height: p.height,
      radius: Math.min(p.width, p.height) / 2,
      rotation: p.rotation,
      cornerRadius: p.radius,
      color:
        p.kind === "chair-back" || p.kind === "chair-arm"
          ? "#D1D5D9"
          : shape.color || "#E8ECEF",
      stroke: "#9CA3AF",
      strokeWidth: 1,
      layerType: "shapes"
    }))
  };
}
export const DYNAMIC_FURNITURE_PRESETS: Furniture[] = [
  { name: "Round table · 6 chairs", layout: DEFAULT_TABLE_LAYOUT },
  {
    name: "Round table · 8 large chairs",
    layout: {
      ...DEFAULT_TABLE_LAYOUT,
      tableWidth: 220,
      chairs: 8,
      chairStyle: "large" as const
    }
  },
  {
    name: "Rectangle · 6 sides + 2 ends",
    layout: {
      ...DEFAULT_TABLE_LAYOUT,
      shape: "rectangle" as const,
      tableWidth: 240,
      tableHeight: 110
    }
  }
].map((p, i) => ({
  id: `dynamic-table-${i}`,
  name: p.name,
  category: "Dynamic tables",
  url: "",
  dynamicTable: p.layout,
  width: layoutDynamicTable(p.layout).width,
  height: layoutDynamicTable(p.layout).height
}));

/** Tabletop in world coordinates, for import anchoring without treating chairs
 * as part of the table or making this furniture a bookable resource. */
export function dynamicTabletop(
  shape: DynamicFurnitureShape
): import("../types").RectangleShape {
  const g = layoutDynamicTable(shape.layout),
    table = g.parts[g.parts.length - 1];
  const sx = (shape.width / g.width) * (shape.scaleX ?? 1),
    sy = (shape.height / g.height) * (shape.scaleY ?? 1);
  const a = ((shape.rotation || 0) * Math.PI) / 180;
  return {
    id: shape.id,
    type: "rectangle",
    layerType: "shapes",
    layerLabel: "table",
    x: shape.x + table.x * sx * Math.cos(a) - table.y * sy * Math.sin(a),
    y: shape.y + table.x * sx * Math.sin(a) + table.y * sy * Math.cos(a),
    width: table.width * sx,
    height: table.height * sy,
    rotation: shape.rotation
  };
}
